import * as React from "react";
import { useTranslation } from "react-i18next";

import { Box, Tooltip, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";

import useSnackbarAlert from "../../../utils/snackbarHandlers";
import { COLORS } from "./constants";
import InventoryTabs from "./InventoryTabs";
// Illustrated stat-card icons exported from Figma (nodes 2083:576 / 597 / 587)
import salesCardIcon from "./assets/card-sales.png";
import activatedCardIcon from "./assets/card-activated.png";
import confirmedCardIcon from "./assets/card-confirmed.png";

interface SalesCalendarProps {
  apiBaseURL: string;
  shopId: any;
  currencySymbol: string;
  onDayClick: (date: string) => void;
  onConfirmClick?: () => void;
  onActivateClick?: () => void;
  onSettlementClick?: () => void;
}

// Default to the icon variant for Activations / Confirms (Sales is always a text
// label). Flip this to true to show text labels instead, per the client note
// that we may want to switch later.
const SHOW_ACTIVATION_CONFIRM_LABELS = false;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "June", "July", "Aug", "Sept", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface DayCell {
  date: string;
  day: number;
  salesAmount: number | null;
  dayEnded: boolean;
  activations: number;
  confirms: number;
  isCurrent: boolean;
}

const SalesCalendar: React.FC<SalesCalendarProps> = ({
  apiBaseURL,
  shopId,
  currencySymbol,
  onDayClick,
  onConfirmClick,
  onActivateClick,
  onSettlementClick,
}) => {
  const { t } = useTranslation("translation");
  const { showErrorMessage } = useSnackbarAlert();

  const now = new Date();
  const [year, setYear] = React.useState<number>(now.getFullYear());
  const [month, setMonth] = React.useState<number>(now.getMonth() + 1); // 1-12
  const [totals, setTotals] = React.useState({ salesTotal: 0, activatedTotal: 0, confirmedTotal: 0 });
  const [days, setDays] = React.useState<DayCell[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    setLoading(true);
    axios
      .post(`${apiBaseURL}/api/inventory-v2/sales/month`, { userId: shopId, year, month })
      .then((res: any) => {
        setTotals(res?.data?.totals ?? { salesTotal: 0, activatedTotal: 0, confirmedTotal: 0 });
        setDays(res?.data?.days ?? []);
      })
      .catch(() => showErrorMessage(t("somethingWentWrong", "Something went wrong")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseURL, shopId, year, month]);

  const leadingBlanks = new Date(Date.UTC(year, month - 1, 1)).getUTCDay(); // 0=Sun

  const money = (v: number) => `${currencySymbol}${Number.isInteger(v) ? v : v.toFixed(2)}`;

  // ---- summary card (Figma nodes 2083:576 / 597 / 587) ----
  // White card with a colored ring + glow: a white icon square on the left, and a
  // colored panel on the right whose title sits on the color with the value in a
  // white rounded inset.
  // Exact Figma spec (node 2083:576): 3px colored border, 25px radius, and a
  // COLORED drop shadow (the card's own color), plus a large icon (~85% of its box).
  const SummaryCard = (props: { title: string; label: string; value: string; color: string; iconSrc: string }) => (
    <Box
      sx={{
        flex: "1 1 260px",
        minWidth: "260px",
        height: "124px",
        display: "flex",
        alignItems: "stretch",
        gap: "8px",
        p: "6px",
        borderRadius: "25px",
        border: `3px solid ${props.color}`,
        backgroundColor: "#fff",
        boxShadow: `0px 4px 20px 0px ${props.color}`,
      }}
    >
      <Box
        sx={{
          width: 88,
          minWidth: 88,
          borderRadius: "20px",
          backgroundColor: "#fff",
          border: `3px solid ${props.color}`,
          boxShadow: `0px 4px 14px 0px ${props.color}80`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: "3px",
        }}
      >
        <Box component='img' src={props.iconSrc} alt={props.title} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
      </Box>
      <Box sx={{ flex: 1, borderRadius: "20px", backgroundColor: props.color, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Typography
          sx={{
            color: "#fff",
            fontWeight: 800,
            fontSize: "18px",
            textAlign: "center",
            py: "6px",
            letterSpacing: "1px",
            // heading band softly floats above the white value area (Figma node 2083:578)
            position: "relative",
            zIndex: 1,
            boxShadow: "0px 3px 8px rgba(0,0,0,0.18)",
          }}
        >
          {props.title}
        </Typography>
        <Box sx={{ flex: 1, m: "5px", borderRadius: "14px", backgroundColor: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontSize: "15px", color: "#000", fontWeight: 700, lineHeight: 1.2 }}>{props.label}</Typography>
          <Typography sx={{ fontSize: "26px", color: "#000", fontWeight: 800, lineHeight: 1.2 }}>{props.value}</Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box p={2}>
      {/* Header: title + tabs */}
      <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap='12px' mb={2} sx={{ borderBottom: "1px solid #e0e0e0", pb: 1 }}>
        <Typography sx={{ fontSize: "30px", fontWeight: 700, color: "#000" }}>{t("salesTitle", "Sales")}</Typography>
        <InventoryTabs active='sales' onConfirmClick={onConfirmClick} onActivateClick={onActivateClick} onSettlementClick={onSettlementClick} />
      </Box>

      {/* Month / year selector */}
      <Box display='flex' alignItems='center' gap='20px' flexWrap='wrap' mb={2}>
        <Typography sx={{ fontSize: "22px", fontWeight: 800, color: COLORS.navy, minWidth: "160px" }}>
          {t("monthlyReport", "Monthly Report")}
        </Typography>
        <Box sx={{ flex: 1, minWidth: "320px", border: `1px solid ${COLORS.calendarBorder}`, borderRadius: "10px", backgroundColor: COLORS.panelBg, p: 1.5 }}>
          <Box display='flex' alignItems='center' justifyContent='center' gap='16px' mb={1}>
            <ChevronLeftIcon sx={{ cursor: "pointer", color: COLORS.blue, fontSize: 28 }} onClick={() => setYear((y) => y - 1)} />
            <Typography sx={{ fontSize: "24px", fontWeight: 800, color: "#000" }}>{year}</Typography>
            <ChevronRightIcon sx={{ cursor: "pointer", color: COLORS.blue, fontSize: 28 }} onClick={() => setYear((y) => y + 1)} />
          </Box>
          <Box display='flex' alignItems='center' justifyContent='center' gap='6px' flexWrap='wrap'>
            {MONTHS.map((m, idx) => {
              const active = idx + 1 === month;
              return (
                <Box
                  key={m}
                  onClick={() => setMonth(idx + 1)}
                  sx={{
                    cursor: "pointer",
                    px: "12px",
                    py: "5px",
                    borderRadius: "8px",
                    fontSize: "16px",
                    fontWeight: active ? 800 : 600,
                    color: active ? COLORS.navy : "#fff",
                    backgroundColor: active ? "#fff" : COLORS.blue,
                    boxShadow: active ? "0 1px 4px rgba(0,0,0,0.25)" : "none",
                  }}
                >
                  {m}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Summary cards */}
      <Box display='flex' gap='16px' flexWrap='wrap' mb={2}>
        <SummaryCard title={t("salesCardTitle", "SALES")} label={t("totalLabel", "Total")} value={money(totals.salesTotal)} color={COLORS.cardSales} iconSrc={salesCardIcon} />
        <SummaryCard title={t("activatedCardTitle", "ACTIVATED")} label={t("totalTicketLabel", "Total Ticket")} value={String(totals.activatedTotal)} color={COLORS.cardActivated} iconSrc={activatedCardIcon} />
        <SummaryCard title={t("confirmedCardTitle", "CONFIRMED")} label={t("totalTicketLabel", "Total Ticket")} value={String(totals.confirmedTotal)} color={COLORS.cardConfirmed} iconSrc={confirmedCardIcon} />
      </Box>

      {/* Calendar grid */}
      <Box sx={{ border: `1px solid ${COLORS.calendarBorder}`, borderRadius: "12px", p: 1.5, opacity: loading ? 0.6 : 1 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "8px" }}>
          {WEEKDAYS.map((w) => (
            <Typography key={w} sx={{ textAlign: "center", fontSize: "17px", color: "#434343", py: 1 }}>
              {w}
            </Typography>
          ))}

          {Array.from({ length: leadingBlanks }).map((_, i) => (
            <Box key={`blank-${i}`} sx={{ minHeight: "150px", borderRadius: "8px" }} />
          ))}

          {days.map((cell) => (
            <Box
              key={cell.date}
              onClick={() => onDayClick(cell.date)}
              sx={{
                minHeight: "150px",
                borderRadius: "8px",
                border: `1px solid ${COLORS.calendarBorder}`,
                background: cell.isCurrent ? `linear-gradient(180deg, ${COLORS.blue}, #6cc0ff)` : "#fff",
                p: "8px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "6px",
                transition: "box-shadow 0.15s",
                "&:hover": { boxShadow: "0 2px 10px rgba(0,0,0,0.18)" },
              }}
            >
              {/* Date badge */}
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  backgroundColor: cell.isCurrent ? COLORS.green : COLORS.blue,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: "15px",
                }}
              >
                {cell.day}
              </Box>

              {/* Sales pill - only for finalized (ended) days */}
              {cell.salesAmount !== null && (
                <Box sx={{ width: "100%", backgroundColor: COLORS.orange, color: "#fff", borderRadius: "14px", textAlign: "center", py: "4px", fontSize: "14px", fontWeight: 600 }}>
                  {t("salesPillLabel", "Sales")}: {money(cell.salesAmount)}
                </Box>
              )}

              {/* Activations / Confirms - hidden when zero */}
              {SHOW_ACTIVATION_CONFIRM_LABELS ? (
                <>
                  {cell.activations > 0 && (
                    <Box sx={{ width: "100%", backgroundColor: COLORS.pillActivation, color: "#fff", borderRadius: "14px", textAlign: "center", py: "4px", fontSize: "13px", fontWeight: 600 }}>
                      {t("activationsLabel", "Activations")}: {cell.activations}
                    </Box>
                  )}
                  {cell.confirms > 0 && (
                    <Box sx={{ width: "100%", backgroundColor: COLORS.pillConfirm, color: "#fff", borderRadius: "14px", textAlign: "center", py: "4px", fontSize: "13px", fontWeight: 600 }}>
                      {t("confirmsLabel", "Confirms")}: {cell.confirms}
                    </Box>
                  )}
                </>
              ) : (
                <Box display='flex' flexDirection='column' gap='4px' sx={{ mt: "2px" }}>
                  {cell.activations > 0 && (
                    <Box display='flex' alignItems='center' gap='8px'>
                      <Tooltip arrow title={t("activationsLabel", "Activations")}>
                        <ConfirmationNumberOutlinedIcon sx={{ color: COLORS.orange, fontSize: 26, cursor: "help" }} />
                      </Tooltip>
                      <Typography sx={{ fontSize: "18px", fontWeight: 800, color: cell.isCurrent ? "#fff" : "#000" }}>{cell.activations}</Typography>
                    </Box>
                  )}
                  {cell.confirms > 0 && (
                    <Box display='flex' alignItems='center' gap='8px'>
                      <Tooltip arrow title={t("confirmsLabel", "Confirms")}>
                        <CheckCircleIcon sx={{ color: COLORS.green, fontSize: 26, cursor: "help" }} />
                      </Tooltip>
                      <Typography sx={{ fontSize: "18px", fontWeight: 800, color: cell.isCurrent ? "#fff" : "#000" }}>{cell.confirms}</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SalesCalendar;
